/**
 * CID Code Types for MEV-Med Frontend
 * Type definitions for CID-10 codes (Classificação Internacional de Doenças) management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de códigos CID-10
 */

/**
 * CID-10 entity interface
 */
export interface CidCode {
  id: number;
  code: string;
  description: string;
  category: string;
  subcategory?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating CID codes
 */
export interface CidCodeCreateFormData {
  code: string;
  description: string;
  category: string;
  subcategory?: string;
}

/**
 * Form data for updating CID codes
 */
export interface CidCodeUpdateFormData {
  code?: string;
  description?: string;
  category?: string;
  subcategory?: string;
  isActive?: boolean;
}

/**
 * Filters for listing CID codes
 */
export interface CidCodeFilters {
  search?: string;
  code?: string;
  category?: string;
  subcategory?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface CidCodeListResponse {
  cidCodes: CidCode[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * CID-10 category info
 */
export interface CidCategory {
  code: string;
  description: string;
}

/**
 * CID-10 catalog statistics
 */
export interface CidCodeStatistics {
  totalCodes: number;
  activeCodes: number;
  inactiveCodes: number;
  categoriesCount: number;
  subcategoriesCount: number;
  codesByCategory: Record<string, number>;
  codesBySubcategory: Record<string, number>;
}

/**
 * Props for UI components
 */
export interface CidCodesTableProps {
  cidCodes: CidCode[];
  onEdit: (cidCode: CidCode) => void;
  onDelete: (id: number) => void;
  onDeactivate?: (id: number) => void;
  onReactivate?: (id: number) => void;
  loading?: boolean;
}

export interface CidCodeFormProps {
  initialData?: CidCode;
  onSubmit: (data: CidCodeCreateFormData | CidCodeUpdateFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
}

export interface CidCodesFiltersProps {
  filters: CidCodeFilters;
  categories: CidCategory[];
  onChange: (filters: CidCodeFilters) => void;
  onSearch: () => void;
}

/**
 * CID-10 Categories with descriptions
 */
export const CID_CATEGORIES: Record<string, string> = {
  'A00-B99': 'Algumas doenças infecciosas e parasitárias',
  'C00-D48': 'Neoplasias [tumores]',
  'D50-D89': 'Doenças do sangue e dos órgãos hematopoiéticos e alguns transtornos imunitários',
  'E00-E90': 'Doenças endócrinas, nutricionais e metabólicas',
  'F00-F99': 'Transtornos mentais e comportamentais',
  'G00-G99': 'Doenças do sistema nervoso',
  'H00-H59': 'Doenças do olho e anexos',
  'H60-H95': 'Doenças do ouvido e da apófise mastóide',
  'I00-I99': 'Doenças do aparelho circulatório',
  'J00-J99': 'Doenças do aparelho respiratório',
  'K00-K93': 'Doenças do aparelho digestivo',
  'L00-L99': 'Doenças da pele e do tecido subcutâneo',
  'M00-M99': 'Doenças do sistema osteomuscular e do tecido conjuntivo',
  'N00-N99': 'Doenças do aparelho geniturinário',
  'O00-O99': 'Gravidez, parto e puerpério',
  'P00-P96': 'Algumas afecções originadas no período perinatal',
  'Q00-Q99': 'Malformações congênitas, deformidades e anomalias cromossômicas',
  'R00-R99': 'Sintomas, sinais e achados anormais de exames clínicos e de laboratório',
  'S00-T98': 'Lesões, envenenamento e algumas outras conseqüências de causas externas',
  'V01-Y98': 'Causas externas de morbidade e de mortalidade',
  'Z00-Z99': 'Fatores que influenciam o estado de saúde e o contato com os serviços de saúde'
};

/**
 * Get list of CID-10 categories as options
 */
export function getCidCategoryOptions(): CidCategory[] {
  return Object.entries(CID_CATEGORIES).map(([code, description]) => ({
    code,
    description
  }));
}

/**
 * Get category description by code
 */
export function getCategoryDescription(categoryCode: string): string {
  return CID_CATEGORIES[categoryCode] || categoryCode;
}

/**
 * Validate CID-10 code format
 * Format: Letter + 2 digits + optional (dot + digit)
 * Examples: I10, M54.9, Z03.8
 */
export function isValidCidCodeFormat(code: string): boolean {
  const cidRegex = /^[A-Z]\d{2}(\.\d)?$/;
  return cidRegex.test(code.trim().toUpperCase());
}

/**
 * Format CID code for display
 */
export function formatCidCode(code: string): string {
  return code.toUpperCase().trim();
}
